import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
import React from 'react';
import { CSSTransition } from 'react-transition-group';
import FocusLock from 'react-focus-lock';
import { isKeyEscape } from "../../../lib/events/keyboard/identifiers";
import * as LayoutEvents from "../../../lib/LayoutEvents";
import { stopPropagation } from "../../../lib/events/stopPropagation";
import { HideBodyVerticalScroll } from "../../../internal/HideBodyVerticalScroll";
import { ModalStack } from "../../../lib/ModalStack";
import { RenderContainer } from "../../../internal/RenderContainer";
import { RenderLayer } from "../../../internal/RenderLayer";
import { ZIndex } from "../../../internal/ZIndex";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { isIE11 } from "../../../lib/client";
import { cx } from "../../../lib/theming/Emotion";
import { SidePageBody } from "../SidePageBody";
import { SidePageContainer } from "../SidePageContainer";
import { SidePageContext } from "../SidePageContext";
import { SidePageFooter } from "../SidePageFooter";
import { SidePageHeader } from "../SidePageHeader";
import { styles } from "../SidePage.styles";
var TRANSITION_TIMEOUT = 200;
/**
 * Сайдпейдж
 *
 * Содержит в себе три компоненты: **SidePage.Header**,
 * **SidePage.Body** и **SidePage.Footer**
 *
 * Для отображения серой плашки в футере в компонент
 * **Footer** необходимо передать пропс **panel**
 */

export var SidePage = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(SidePage, _React$Component);

  function SidePage() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.state = {
      hasHeader: false,
      hasFooter: false,
      hasPanel: false
    };
    _this.theme = void 0;
    _this.stackSubscription = null;
    _this.layoutRef = null;
    _this.footer = null;

    _this.updateLayout = function () {
      if (_this.footer) {
        _this.footer.update();
      }
    };

    _this.getSidePageContextProps = function () {
      return {
        hasHeader: _this.state.hasHeader,
        hasFooter: _this.state.hasFooter,
        hasPanel: _this.state.hasPanel,
        requestClose: _this.requestClose,
        getWidth: _this.getWidth,
        updateLayout: _this.updateLayout,
        footerRef: _this.footerRef,
        setHasHeader: _this.setHasHeader,
        setHasFooter: _this.setHasFooter,
        setHasPanel: _this.setHasPanel
      };
    };

    _this.getWidth = function () {
      if (!_this.layoutRef) {
        return 'auto';
      }

      return _this.layoutRef.getBoundingClientRect().width;
    };

    _this.handleStackChange = function (stack) {
      var sidePages = stack.filter(function (x) {
        return x instanceof SidePage && x.props.fromLeft === _this.props.fromLeft;
      });
      var currentSidePagePosition = sidePages.indexOf(_assertThisInitialized(_this));
      var hasMargin = sidePages.length > 1 && currentSidePagePosition === sidePages.length - 1;
      var hasShadow = sidePages.length < 3 || currentSidePagePosition > sidePages.length - 3;
      var hasBackground = ModalStack.isBlocking(_assertThisInitialized(_this));

      _this.setState({
        stackPosition: stack.indexOf(_assertThisInitialized(_this)),
        hasMargin: hasMargin,
        hasShadow: hasShadow,
        hasBackground: hasBackground
      });
    };

    _this.handleClickOutside = function (e) {
      if (_this.state.stackPosition === 0 && !_this.props.ignoreBackgroundClick) {
        // ignore mousedown on window scrollbar
        if (e instanceof MouseEvent && e.clientX > document.documentElement.clientWidth) {
          return;
        }

        _this.requestClose();
      }
    };

    _this.handleKeyDown = function (e) {
      if (_this.state.stackPosition !== 0) {
        return;
      }

      if (isKeyEscape(e)) {
        stopPropagation(e);

        _this.requestClose();
      }
    };

    _this.requestClose = function () {
      if (_this.props.disableClose) {
        return;
      }

      if (_this.props.onClose) {
        _this.props.onClose();
      }
    };

    _this.footerRef = function (ref) {
      _this.footer = ref;
    };

    _this.setHasHeader = function (hasHeader) {
      if (hasHeader === void 0) {
        hasHeader = true;
      }

      _this.state.hasHeader !== hasHeader && _this.setState({
        hasHeader: hasHeader
      });
    };

    _this.setHasFooter = function (hasFooter) {
      if (hasFooter === void 0) {
        hasFooter = true;
      }

      _this.state.hasFooter !== hasFooter && _this.setState({
        hasFooter: hasFooter
      });
    };

    _this.setHasPanel = function (hasPanel) {
      if (hasPanel === void 0) {
        hasPanel = false;
      }

      _this.state.hasPanel !== hasPanel && _this.setState({
        hasPanel: hasPanel
      });
    };

    return _this;
  }

  var _proto = SidePage.prototype;

  _proto.componentDidMount = function componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
    this.stackSubscription = ModalStack.add(this, this.handleStackChange);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);

    if (this.stackSubscription != null) {
      this.stackSubscription.remove();
    }

    ModalStack.remove(this);
  }
  /**
  * Обновляет разметку компонента.
  * @public
  */
  ;

  _proto.render = function render() {
    var _this2 = this;

    return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
      _this2.theme = theme;
      return _this2.renderMain();
    });
  };

  _proto.renderMain = function renderMain() {
    var _this$props = this.props,
        blockBackground = _this$props.blockBackground,
        disableAnimations = _this$props.disableAnimations;
    return /*#__PURE__*/React.createElement(CommonWrapper, this.props, /*#__PURE__*/React.createElement(RenderContainer, null, /*#__PURE__*/React.createElement("div", null, blockBackground && this.renderShadow(), /*#__PURE__*/React.createElement(CSSTransition, {
      "in": true,
      classNames: this.getTransitionNames(),
      appear: !disableAnimations,
      enter: !disableAnimations,
      exit: false,
      timeout: {
        enter: TRANSITION_TIMEOUT,
        exit: TRANSITION_TIMEOUT
      }
    }, this.renderContainer()))));
  };

  _proto.renderContainer = function renderContainer() {
    var _cx,
        _cx2,
        _this3 = this;

    var _this$props2 = this.props,
        width = _this$props2.width,
        blockBackground = _this$props2.blockBackground,
        fromLeft = _this$props2.fromLeft,
        disableFocusLock = _this$props2.disableFocusLock;
    return /*#__PURE__*/React.createElement(ZIndex, {
      priority: 'Sidepage',
      "data-tid": "SidePage__root",
      className: cx((_cx = {}, _cx[styles.root()] = true, _cx[styles.leftSide()] = Boolean(fromLeft), _cx)),
      onScroll: LayoutEvents.emit,
      createStackingContext: true,
      style: {
        width: width || (blockBackground ? 800 : 500)
      }
    }, /*#__PURE__*/React.createElement(RenderLayer, {
      onClickOutside: this.handleClickOutside,
      active: true
    }, /*#__PURE__*/React.createElement("div", {
      "data-tid": "SidePage__container",
      className: cx(styles.wrapper(this.theme), (_cx2 = {}, _cx2[styles.shadow(this.theme)] = this.state.hasShadow, _cx2[styles.wrapperLeft()] = fromLeft, _cx2)),
      style: this.getSidebarStyle()
    }, /*#__PURE__*/React.createElement(FocusLock, {
      disabled: disableFocusLock,
      autoFocus: false
    }, /*#__PURE__*/React.createElement("div", {
      ref: function ref(_) {
        return _this3.layoutRef = _;
      },
      className: styles.layout()
    }, /*#__PURE__*/React.createElement(SidePageContext.Provider, {
      value: this.getSidePageContextProps()
    }, this.props.children))))));
  };

  _proto.renderShadow = function renderShadow() {
    var _cx3;

    return /*#__PURE__*/React.createElement(ZIndex, {
      priority: 'Sidepage',
      className: styles.overlay(),
      onScroll: LayoutEvents.emit
    }, /*#__PURE__*/React.createElement(HideBodyVerticalScroll, {
      key: "hbvs"
    }), /*#__PURE__*/React.createElement("div", {
      key: "overlay",
      className: cx((_cx3 = {}, _cx3[styles.background()] = true, _cx3[styles.backgroundGray(this.theme)] = this.state.hasBackground, _cx3))
    }));
  };

  _proto.getSidebarStyle = function getSidebarStyle() {
    var sidePageStyle = {};

    if (this.state.hasMargin) {
      if (this.props.fromLeft) {
        sidePageStyle.marginLeft = 20;
      } else {
        sidePageStyle.marginRight = 20;
      }
    }

    return sidePageStyle;
  };

  _proto.getTransitionNames = function getTransitionNames() {
    var transition = this.props.fromLeft ? styles.transitionRight : styles.transitionLeft;
    return {
      enter: transition(),
      enterActive: styles.transitionActive(),
      exit: styles.transitionLeave(),
      exitActive: styles.transitionLeaveActive(),
      appear: transition(),
      appearActive: styles.transitionActive()
    };
  };

  return SidePage;
}(React.Component);
SidePage.__KONTUR_REACT_UI__ = 'SidePage';
SidePage.Header = SidePageHeader;
SidePage.Body = SidePageBody;
SidePage.Footer = SidePageFooter;
SidePage.Container = SidePageContainer;
SidePage.defaultProps = {
  // NOTE: в ie нормально не работает
  disableFocusLock: isIE11
};